import { useCallback } from 'react';

import { largeUploadByIdSelector, largeUploadUrlInfoSelector, type Upload } from '@air/redux-uploader';
import { useCreateLargeUpload } from '@air/upload-utils';

import { type PrivateUploadTaskParams } from '~/components/Upload/hooks/types';
import { usePublicUploadUrlsManager } from '~/providers/PublicUploadUrlsManagerProvider';
import { useAirStore } from '~/utils/ReduxUtils';

type CreateLargePublicUploadParams = {
  uploadId: Upload['id'];
  onUploadStarted: PrivateUploadTaskParams['onUploadStarted'];
  onError: PrivateUploadTaskParams['onError'];
};

export const useCreateLargePublicUpload = () => {
  const store = useAirStore();
  const { createLargeUpload } = useCreateLargeUpload();
  const { manager: uploadUrlsManager } = usePublicUploadUrlsManager();

  const createLargePublicUpload = useCallback(
    async ({ onUploadStarted, uploadId, onError }: CreateLargePublicUploadParams) => {
      const upload = largeUploadByIdSelector(store.getState(), uploadId);
      if (upload) {
        await createLargeUpload({
          getLargeUploadUrlInfo: async () => {
            const uploadUrlInfo = largeUploadUrlInfoSelector(store.getState(), uploadId);
            return uploadUrlInfo ?? (await uploadUrlsManager.getLargeUploadUrl(uploadId));
          },
          onError,
          upload,
          onUploadStarted,
        });
      }
    },
    [createLargeUpload, store, uploadUrlsManager.getLargeUploadUrl],
  );

  return {
    createLargePublicUpload,
  };
};
